import { AlertTriangle, TrendingUp } from 'lucide-react';
import { motion, useReducedMotion } from 'framer-motion';

import type { AIProvider } from '@aicostguard-dev/shared';
import { PROVIDER_BRAND_ICON } from '@/components/icons/ProviderIcons';
import { PROVIDER_INFO } from '@/constants/providers';
import { useCountUp } from './useCountUp';

/** Upward-trending demo sparkline, 320×96 viewBox. */
const SPARK_LINE =
  'M2 80L23 76L44 78L66 70L87 72L108 64L130 66L151 54L172 58L194 46L215 50L236 38L258 40L279 28L300 30L318 18';
const SPARK_AREA = `${SPARK_LINE}L318 96L2 96Z`;

/** Demo per-provider amounts — sum exactly to the $1,284.07 headline number. */
const CHIPS: Array<{ provider: AIProvider; amount: string }> = [
  { provider: 'openai', amount: '$612.40' },
  { provider: 'anthropic', amount: '$389.22' },
  { provider: 'gemini', amount: '$174.91' },
  { provider: 'mistral', amount: '$107.54' },
];

const SPEND_TARGET = 1284.07;

/**
 * Animated product preview shown in the hero. Pure DOM/SVG — no images, no
 * chart library. All animations are one-shot on mount; under
 * prefers-reduced-motion the final state renders statically.
 * Decorative (demo data), so hidden from assistive tech.
 */
export function HeroDashboard() {
  const reduce = useReducedMotion() ?? false;
  const spend = useCountUp(SPEND_TARGET, {
    enabled: !reduce,
    duration: 900,
    delay: 250,
  });

  const appear = (delay: number) =>
    reduce
      ? {}
      : {
          initial: { opacity: 0, y: 8 },
          animate: { opacity: 1, y: 0 },
          transition: { duration: 0.35, ease: 'easeOut' as const, delay },
        };

  return (
    <div
      className="relative isolate mx-auto w-full max-w-[460px] lg:ml-auto"
      aria-hidden="true"
    >
      {/* Ambient glow behind the window */}
      <div className="absolute -inset-6 -z-10 rounded-[2rem] bg-primary/15 blur-2xl" />

      <motion.div
        className="glass-card rounded-2xl p-5 sm:p-6"
        {...(reduce
          ? {}
          : {
              initial: { opacity: 0, y: 16, scale: 0.97 },
              animate: { opacity: 1, y: 0, scale: 1 },
              transition: { duration: 0.45, ease: 'easeOut' as const },
            })}
      >
        {/* Window chrome */}
        <div className="flex items-center justify-between border-b border-hairline pb-3">
          <div className="flex items-center gap-2">
            <span className="flex gap-1.5">
              <span className="size-2.5 rounded-full bg-muted-foreground/25" />
              <span className="size-2.5 rounded-full bg-muted-foreground/25" />
              <span className="size-2.5 rounded-full bg-muted-foreground/25" />
            </span>
            <span className="ml-2 font-mono text-[11px] text-muted-foreground">
              aicostr · live
            </span>
          </div>
          <span className="inline-flex items-center gap-1.5 rounded-full border border-success/30 bg-success/10 px-2 py-0.5 text-[10px] font-medium text-success">
            <span className="size-1.5 rounded-full bg-success motion-safe:animate-pulse" />
            Live
          </span>
        </div>

        {/* Headline spend */}
        <div className="pt-4">
          <p className="text-[11px] font-medium uppercase tracking-[0.18em] text-muted-foreground">
            AI spend · this month
          </p>
          <div className="mt-1.5 flex flex-wrap items-baseline gap-x-3 gap-y-1">
            <span className="font-mono text-3xl font-semibold tabular-nums tracking-tight sm:text-4xl">
              <span className="text-muted-foreground">$</span>
              <span className="inline-block min-w-[8ch] text-left">
                {spend.toLocaleString('en-US', {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </span>
            </span>
            <motion.span
              className="inline-flex items-center gap-1 text-xs font-medium text-success"
              {...appear(0.9)}
            >
              <TrendingUp className="size-3.5" />
              12.4% vs last week
            </motion.span>
          </div>
        </div>

        {/* Self-drawing sparkline */}
        <div className="mt-4">
          <svg viewBox="0 0 320 96" className="h-24 w-full" preserveAspectRatio="none">
            <defs>
              <linearGradient
                id="aicostr-spark"
                x1="0"
                y1="0"
                x2="0"
                y2="96"
                gradientUnits="userSpaceOnUse"
              >
                <stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity="0.22" />
                <stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity="0" />
              </linearGradient>
            </defs>
            <motion.g
              {...(reduce
                ? {}
                : {
                    initial: { opacity: 0 },
                    animate: { opacity: 1 },
                    transition: { duration: 0.3, ease: 'easeOut' as const, delay: 1.1 },
                  })}
            >
              <path d={SPARK_AREA} fill="url(#aicostr-spark)" />
              <circle cx="318" cy="18" r="7" fill="hsl(var(--primary) / 0.25)" />
              <circle cx="318" cy="18" r="3" fill="hsl(var(--primary))" />
            </motion.g>
            <motion.path
              d={SPARK_LINE}
              fill="none"
              stroke="hsl(var(--primary))"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              {...(reduce
                ? {}
                : {
                    initial: { pathLength: 0 },
                    animate: { pathLength: 1 },
                    transition: { duration: 0.85, ease: 'easeInOut' as const, delay: 0.4 },
                  })}
            />
          </svg>
        </div>

        {/* Per-provider chips */}
        <div className="mt-4 grid grid-cols-2 gap-2">
          {CHIPS.map(({ provider, amount }, i) => {
            const Icon = PROVIDER_BRAND_ICON[provider];
            return (
              <motion.div
                key={provider}
                className="flex items-center justify-between gap-2 rounded-lg border border-hairline bg-background/50 px-3 py-2"
                {...appear(0.55 + i * 0.08)}
              >
                <span className="flex min-w-0 items-center gap-2">
                  <Icon className="size-4 shrink-0" />
                  <span className="truncate text-xs font-medium">
                    {PROVIDER_INFO[provider].displayName}
                  </span>
                </span>
                <span className="font-mono text-[11px] tabular-nums text-muted-foreground">
                  {amount}
                </span>
              </motion.div>
            );
          })}
        </div>

        {/* Anomaly toast */}
        <motion.div
          className="mt-3 flex items-center gap-2 rounded-lg border border-warning/30 bg-warning/10 px-3 py-2"
          {...appear(1.15)}
        >
          <AlertTriangle className="size-3.5 shrink-0 text-warning" />
          <span className="truncate text-xs">
            <span className="font-medium text-foreground">Anomaly caught</span>
            <span className="text-muted-foreground"> — 2.1× baseline · Slack alerted</span>
          </span>
        </motion.div>
      </motion.div>
    </div>
  );
}
